import { cn } from '../../lib/utils/cn'
import { Button } from '../../components/ui/Button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/skeleton/Card'
import TextInput from '../../components/FormElements/TextInput'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthLogin } from '../../api/auth/useAuthLogin'
import { useDispatch } from 'react-redux'
import { queryClient } from '../../main'
import { setAuthenticated, setUser } from '../../store/reducers/userReducer'
import toast from 'react-hot-toast'

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  const navigate = useNavigate()
  const { mutateAsync: authLogin } = useAuthLogin()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [searchParams] = useSearchParams()

  const formContext = useForm({
    mode: 'onSubmit',
  })
  const { handleSubmit } = formContext

  const onSubmit = async (values: FieldValues): Promise<void> => {
    setIsLoading(true)

    await authLogin({
      email: values.email,
      password: values.password,
    })
      .then(async (response) => {
        await queryClient.resetQueries(['currentUser'])
        await dispatch(setUser(response))
        await dispatch(setAuthenticated(true))

        navigate('/dashboard')
      })
      .catch((err): void => {
        const message = err.response.data.error?.message || 'Missing credentials'
        toast.error(`Error: ${message}`)
      })
      .finally(() => setIsLoading(false))
  }

  const google_oauth_success = useMemo(
    () => searchParams.get('google_oauth_success'),
    [searchParams],
  )

  useEffect(() => {
    if (google_oauth_success === 'true') {
      dispatch({
        type: 'user/SET_STATE',
        payload: {
          id: 1,
          avatar: '',
          authorized: true,
          isAuthenticated: true,
        },
      })
      navigate('/dashboard')
    } else if (google_oauth_success === 'false') {
      toast.error('Google user not found')
    }
  }, [dispatch, google_oauth_success, navigate])

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10 h-screen bg-foreground/50 dark:bg-slate-700">
      <div className="w-full max-w-sm">
        <div className={cn('flex flex-col gap-6', className)} {...props}>
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Login</CardTitle>
              <CardDescription>
                Enter your email below to login to your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormProvider {...formContext}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex flex-col gap-6">
                    <div className="grid gap-2">
                      <TextInput
                        label="Email"
                        name={'email'}
                        type="email"
                        placeholder="johndoe@example.com"
                      />
                    </div>
                    <div className="grid gap-2">
                      <TextInput label="Password" name={'password'} type="password" />
                      {/* <a
                        href="forgot-password"
                        className="ml-auto inline-block text-sm underline-offset-4 hover:underline">
                        Forgot your password?
                      </a> */}
                    </div>
                    <Button type="submit" className="w-full">
                      {isLoading ? 'Logging in...' : 'Login'}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={isLoading}
                      onClick={(e) => {
                        e.preventDefault()
                        window.location.href = `${
                          import.meta.env.VITE_API_URL
                        }/auth/google/login?scope=email profile`
                      }}>
                      Login with Google
                    </Button>
                  </div>
                  <div className="mt-4 text-center text-sm">
                    Don&apos;t have an account?{' '}
                    <a href="signup" className="underline underline-offset-4">
                      Sign up
                    </a>
                  </div>
                </form>
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
